import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';
import DataFlowAnimation from './DataFlowAnimation';

const HeroSection = () => {
  const navigate = useNavigate();

  return (
    <section className="relative pt-20 pb-20 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-blue-50 rounded-full blur-3xl opacity-30 transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-indigo-50 rounded-full blur-3xl opacity-30 transform -translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Heading */}
          <h1 className="text-5xl md:text-[60px] md:leading-tight font-bold text-gray-900 mb-6 leading-tight animate-fade-in-up">
            The Revenue OS
            <span className="block">that tells your team</span>
            <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent pb-2">exactly what to do next</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed animate-fade-in-up animation-delay-200">
            Forsyt sits on top of your CRM, calls, email, and Slack to identify the 1 deal that truly matters for each rep, pinpoints the real blocker, and prescribes the exact next action, automatically.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12 animate-fade-in-up animation-delay-400">
            <a 
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold rounded-full border-2 border-gray-300 hover:border-blue-600 bg-white hover:bg-gray-50 transition-all duration-300"
              data-testid="continue-btn"
            >
              <Play className="mr-2 h-5 w-5" />
              Continue
            </a>
            <button 
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-sm font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              data-testid="see-how-btn"
            >
              See How
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>

          {/* Data Flow Animation */}
          <DataFlowAnimation />

          {/* Trust indicators */}
          <div className="flex flex-col items-center justify-center gap-1 text-sm text-gray-500 animate-fade-in-up animation-delay-600">
            <span className="font-semibold text-gray-700">Backed by T9L.</span>
            <span>Forsyt is now live for 5 enterprises and 100+ signups in pipeline.</span>
          </div>

          {/* As Seen On strip */}
          <div className="mt-8 animate-fade-in-up animation-delay-600 w-full" data-testid="as-seen-on">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-8 bg-blue-50 border border-blue-100 rounded-xl px-4 sm:px-8 py-4 sm:py-5">
              <p className="text-[11px] sm:text-xs font-bold text-blue-600 uppercase tracking-widest whitespace-nowrap">As seen on</p>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-items-center sm:justify-center gap-x-4 gap-y-3 sm:gap-8 w-full sm:w-auto">
              {[
                { name: 'The Tribune', logo: '/assets/9wr1nkqb.png', href: 'https://www.tribuneindia.com/topic/revenue-automation', scale: 1.0 },
                { name: 'Business Standard', logo: '/assets/14jtgezk.svg', href: 'https://www.business-standard.com/content/press-releases-ani/t9l-venture-studio-backs-revenue-os-startup-forsyt-with-strategic-investment-126040200434_1.html', scale: 1.0 },
                { name: 'News18', logo: '/assets/ndrnzp2k.png', href: 'https://www.news18.com/agency-feeds/t9l-venture-studio-backs-revenue-os-startup-forsyt-with-strategic-investment-10010724.html', scale: 1.0 },
                { name: 'ANI', logo: '/assets/io3ryz8h.png', href: 'https://www.aninews.in/news/business/t9l-venture-studio-backs-revenue-os-startup-forsyt-with-strategic-investment20260402112112', scale: 1.0 },
              ].map((outlet) => (
                <a
                  key={outlet.name}
                  href={outlet.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="opacity-75 hover:opacity-100 transition-opacity duration-200"
                  data-testid={`press-${outlet.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="h-9 sm:h-12 w-24 sm:w-32 flex items-center justify-center overflow-hidden">
                    <img
                      src={outlet.logo}
                      alt={`${outlet.name} press coverage of Forsyt Revenue OS`}
                      className="max-h-full max-w-full object-contain"
                      style={{ transform: `scale(${outlet.scale})` }}
                    />
                  </div>
                </a>
              ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
